import { Platform } from 'react-native';
import { showError, showInfo, showSuccess } from '../../shared/ui/appToast';
import { captureSentryError } from '../../shared/sentry/sentryUser';
import { getMpIntegrationStatus } from './mpSettingsRepo';
import {
  MERCADOPAGO_OAUTH_RETURN_URL_NATIVE,
  MP_OAUTH_RELOAD_FLAG,
  MP_OAUTH_RETURN_BRANCH_ID_KEY,
  MP_OAUTH_RETURN_SHELL_ROUTE_KEY,
  type MercadoPagoOAuthReturnShellRoute,
  resolveMercadoPagoOAuthReturnUrl,
} from './mercadopagoConnectService';

export const MERCADOPAGO_OAUTH_CALLBACK_PREFIX = MERCADOPAGO_OAUTH_RETURN_URL_NATIVE;

export const MP_OAUTH_POST_MESSAGE_TYPE = 'vuelto:mp-oauth-callback' as const;

let lastHandledUrl = '';
let lastHandledAt = 0;

function decodeDesc(s: string) {
  try {
    return decodeURIComponent(s.replace(/\+/g, ' '));
  } catch {
    return s;
  }
}

function isMercadoPagoOAuthReturnUrl(url: string): boolean {
  if (url.startsWith(MERCADOPAGO_OAUTH_RETURN_URL_NATIVE)) return true;
  if (url.startsWith(`${MERCADOPAGO_OAUTH_RETURN_URL_NATIVE}?`)) return true;

  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    const path = parsed.pathname.replace(/\/$/, '') || '/';
    if (path !== '/mp-oauth/callback') return false;

    const expectedOrigin =
      process.env.EXPO_PUBLIC_EXPO_WEB_APP_ORIGIN?.trim().replace(/\/$/, '') ||
      (Platform.OS === 'web' && typeof window !== 'undefined' ? window.location.origin : 'https://app.vuelto.ar');
    return parsed.origin.toLowerCase() === expectedOrigin.toLowerCase();
  } catch {
    return false;
  }
}

function markMercadoPagoOAuthConnected(branchId: string | null): void {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(MP_OAUTH_RELOAD_FLAG, '1');
    if (branchId) {
      sessionStorage.setItem(MP_OAUTH_RETURN_BRANCH_ID_KEY, branchId);
    }
  } catch {
    /* ignore */
  }
}

function stripOAuthCallbackFromHistory(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  const path = window.location.pathname.replace(/\/$/, '') || '/';
  if (path !== '/mp-oauth/callback') return;
  const clean = `${window.location.origin}/`;
  window.history.replaceState({}, document.title, clean);
}

export type MercadoPagoOAuthPostRedirect = {
  shellRoute: MercadoPagoOAuthReturnShellRoute;
  branchId: string | null;
};

/** Tras callback web en la misma ventana: pantalla shell a la que volver. */
export function consumeMercadoPagoOAuthPostRedirect(): MercadoPagoOAuthPostRedirect | null {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return null;
  try {
    const reloadPending = sessionStorage.getItem(MP_OAUTH_RELOAD_FLAG) === '1';
    const shellRouteRaw = sessionStorage.getItem(MP_OAUTH_RETURN_SHELL_ROUTE_KEY)?.trim();
    if (!reloadPending && !shellRouteRaw) return null;

    const shellRoute: MercadoPagoOAuthReturnShellRoute =
      shellRouteRaw === 'MercadoPagoBranchSettings' ? 'MercadoPagoBranchSettings' : 'BranchSettings';
    const branchId = sessionStorage.getItem(MP_OAUTH_RETURN_BRANCH_ID_KEY)?.trim() || null;

    sessionStorage.removeItem(MP_OAUTH_RETURN_SHELL_ROUTE_KEY);

    return { shellRoute, branchId };
  } catch {
    return null;
  }
}

export function consumeMercadoPagoOAuthReloadPending(): boolean {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return false;
  try {
    const pending = sessionStorage.getItem(MP_OAUTH_RELOAD_FLAG) === '1';
    if (pending) {
      sessionStorage.removeItem(MP_OAUTH_RELOAD_FLAG);
    }
    return pending;
  } catch {
    return false;
  }
}

/**
 * Procesa el deep link o la URL devuelta por `openAuthSessionAsync` tras OAuth MP.
 * Evita toasts duplicados si el sistema entrega la misma URL por Linking y por el resultado del browser.
 */
export function handleMercadoPagoOAuthReturnUrl(url: string): boolean {
  if (!isMercadoPagoOAuthReturnUrl(url)) return false;

  const now = Date.now();
  if (url === lastHandledUrl && now - lastHandledAt < 4000) {
    return true;
  }
  lastHandledUrl = url;
  lastHandledAt = now;

  let u: URL;
  try {
    u = new URL(url);
  } catch {
    showError('Mercado Pago', 'El link de vuelta no es válido.');
    return true;
  }

  const branchId = u.searchParams.get('branch_id')?.trim() || null;

  const err = u.searchParams.get('error');
  if (err) {
    const desc = u.searchParams.get('error_description');
    showError('Mercado Pago', desc ? decodeDesc(desc) : err);
    stripOAuthCallbackFromHistory();
    return true;
  }

  if (u.searchParams.get('mp_connected') === '1') {
    // El param es claim del redirect, no prueba: el estado real vive server-side.
    // Confirmar con mp_integration_status_get antes de mostrar éxito y marcar reload.
    if (branchId) {
      showInfo('Mercado Pago', 'Verificando conexión…');
      void confirmMercadoPagoOAuthConnection(branchId);
    } else {
      showInfo('Mercado Pago', 'Conexión en proceso. Revisá el estado en Integraciones.');
    }
    stripOAuthCallbackFromHistory();
    return true;
  }

  stripOAuthCallbackFromHistory();
  return true;
}

/** Verifica la conexión contra el server antes de declarar éxito (#1285). */
async function confirmMercadoPagoOAuthConnection(branchId: string): Promise<void> {
  try {
    const status = await getMpIntegrationStatus(branchId);
    if (status.oauth_connected) {
      markMercadoPagoOAuthConnected(branchId);
      showSuccess(
        'Mercado Pago',
        'Cuenta conectada. Elegí una caja o pegá el ID de este local.'
      );
      return;
    }
    captureSentryError(
      new Error('OAuth return con mp_connected=1 pero oauth_connected=false'),
      { tags: { module: 'mercadopago', op: 'oauth.verify' } }
    );
    showError('Mercado Pago', 'No pudimos confirmar la conexión. Revisá Integraciones.');
  } catch (e) {
    captureSentryError(e, { tags: { module: 'mercadopago', op: 'oauth.verify' } });
    showError('Mercado Pago', 'No pudimos verificar la conexión. Revisá Integraciones.');
  }
}

/** Prefijo esperado para el retorno OAuth en la plataforma actual (nativo o web). */
export function mercadoPagoOAuthReturnPrefix(): string {
  return resolveMercadoPagoOAuthReturnUrl();
}
